import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';
import RichTextEditor from '../components/RichTextEditor';

interface ContestPageProps {
  onNavigate: (page: string, data?: any) => void;
  user: any;
}

const GENRES = [
  'Romance', 'Fantasy', 'Sci-Fi', 'Mystery', 'Thriller',
  'Horror', 'Literary Fiction', 'Slice of Life', 'Adventure',
  'Historical', 'Comedy', 'Drama', 'General',
];

function CountdownTimer({ targetDate }: { targetDate: string }) {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const update = () => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const diff = target - now;

      if (diff <= 0) {
        setTimeLeft('Ended');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const secs = Math.floor((diff % (1000 * 60)) / 1000);

      if (days > 0) setTimeLeft(`${days}d ${hours}h ${mins}m`);
      else setTimeLeft(`${hours}h ${mins}m ${secs}s`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  return <span>{timeLeft}</span>;
}

function getWordCount(html: string): number {
  const text = html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ');
  const words = text.trim().split(/\s+/).filter(w => w.length > 0);
  return words.length;
}

export default function ContestPage({ onNavigate, user }: ContestPageProps) {
  const [contests, setContests] = useState<any[]>([]);
  const [selectedContest, setSelectedContest] = useState<any>(null);
  const [selectedEntry, setSelectedEntry] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'current' | 'entries' | 'submit' | 'past' | 'read' | 'admin'>('current');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [genre, setGenre] = useState('General');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pastContests, setPastContests] = useState<any[]>([]);
  const readStartTime = useRef<number>(0);
  const readTracked = useRef(false);
  const [adminForm, setAdminForm] = useState({
    title: '', description: '', theme: '', rules: '',
    prizePool: 85, firstPrize: 50, secondPrize: 25, thirdPrize: 10,
    startDate: '', endDate: '', judgeEndDate: '',
    minWordCount: 500, maxWordCount: 5000,
  });
  const [editingContestId, setEditingContestId] = useState<number | null>(null);

  useEffect(() => {
    loadContests();
  }, []);

  useEffect(() => {
    if (tab === 'read' && selectedEntry) {
      readStartTime.current = Date.now();
      readTracked.current = false;

      const trackInterval = setInterval(() => {
        if (!readTracked.current && user && selectedEntry) {
          const elapsed = Math.floor((Date.now() - readStartTime.current) / 1000);
          if (elapsed >= 30) {
            api.contests.trackRead(selectedEntry.id, elapsed).catch(() => {});
            readTracked.current = true;
          }
        }
      }, 5000);

      return () => {
        clearInterval(trackInterval);
        if (user && selectedEntry && readStartTime.current > 0) {
          const elapsed = Math.floor((Date.now() - readStartTime.current) / 1000);
          if (elapsed >= 5) {
            api.contests.trackRead(selectedEntry.id, elapsed).catch(() => {});
          }
        }
      };
    }
  }, [tab, selectedEntry, user]);

  const loadContests = async () => {
    try {
      setLoading(true);
      const [all, past] = await Promise.all([
        api.contests.list(),
        api.contests.pastWinners(),
      ]);
      setContests(all);
      setPastContests(past);

      const current = all.find((c: any) => c.status === 'OPEN' || c.status === 'JUDGING') || all[0];
      if (current) {
        const detail = await api.contests.get(current.id);
        setSelectedContest(detail);
        if (detail.userEntry) {
          setTitle(detail.userEntry.title);
          setContent(detail.userEntry.content);
          setGenre(detail.userEntry.genre || 'General');
        }
      }
    } catch (err) {
      console.error('Failed to load contests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedContest) return;
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      if (selectedContest.userEntry) {
        await api.contests.updateEntry(selectedContest.id, { title, content, genre });
        setSuccess('Entry updated successfully!');
      } else {
        await api.contests.enter(selectedContest.id, { title, content, genre });
        setSuccess('Entry submitted successfully!');
      }
      const detail = await api.contests.get(selectedContest.id);
      setSelectedContest(detail);
    } catch (err: any) {
      setError(err.message || 'Failed to submit entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (entryId: number) => {
    if (!user) {
      onNavigate('login');
      return;
    }
    try {
      await api.contests.vote(entryId);
      const detail = await api.contests.get(selectedContest.id);
      setSelectedContest(detail);
    } catch (err: any) {
      setError(err.message || 'Failed to vote');
    }
  };

  const readEntry = async (entryId: number) => {
    try {
      const entry = await api.contests.getEntry(selectedContest.id, entryId);
      setSelectedEntry(entry);
      setTab('read');
    } catch (err) {
      console.error('Failed to load entry:', err);
    }
  };

  const wordCount = getWordCount(content);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-10 h-10 border-3 border-ink-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!selectedContest && contests.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <div className="w-24 h-24 rounded-full bg-amber-900/20 border border-amber-700/30 flex items-center justify-center text-5xl mx-auto mb-6">
            🏆
          </div>
          <h1 className="text-3xl font-bold mb-3">Monthly Writing Contests</h1>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            Our first contest is coming soon! ScribeHaven hosts monthly writing challenges with real prizes for the top stories.
          </p>
        </div>

        {/* What to expect preview */}
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          {[
            { icon: '✏️', title: 'Write a Short Story', desc: 'Submit an original story within the word limit on the monthly theme' },
            { icon: '👥', title: 'Community Voting', desc: 'Readers vote for their favourite entries during the judging period' },
            { icon: '💰', title: 'Win Prizes', desc: 'Top entries earn Ink currency, badges, and featured placement on the homepage' },
          ].map(card => (
            <div key={card.title} className="bg-gray-900 border border-gray-800 rounded-xl p-5 text-center">
              <span className="text-3xl block mb-3">{card.icon}</span>
              <h3 className="font-bold mb-1">{card.title}</h3>
              <p className="text-sm text-gray-400">{card.desc}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <p className="text-gray-500 text-sm mb-4">Want to be notified when the first contest launches?</p>
          <button
            onClick={() => onNavigate('discover')}
            className="px-6 py-3 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-xl font-medium transition-colors text-gray-300"
          >
            Explore Stories While You Wait
          </button>
        </div>
      </div>
    );
  }

  if (tab === 'read' && selectedEntry) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button
          onClick={() => { setSelectedEntry(null); setTab('entries'); }}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to entries
        </button>

        <article className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
          <h1 className="text-3xl font-bold mb-3">{selectedEntry.title}</h1>
          <div className="flex flex-wrap items-center gap-3 text-gray-400 text-sm mb-6">
            <span>by {selectedEntry.user.username}</span>
            <span>·</span>
            <span className="px-2 py-0.5 bg-gray-800 rounded-full text-xs">{selectedEntry.genre}</span>
            <span>·</span>
            <span>{selectedEntry.wordCount.toLocaleString()} words</span>
            <span>·</span>
            <span>{selectedEntry._count?.entryReads || 0} reads</span>
            <span>·</span>
            <span>{selectedEntry._count?.contestVotes || 0} votes</span>
          </div>
          <div
            className="prose prose-invert max-w-none font-serif text-lg leading-relaxed
              [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-6 [&_h2]:mb-3
              [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2
              [&_blockquote]:border-l-4 [&_blockquote]:border-ink-500 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-400 [&_blockquote]:my-4
              [&_hr]:border-gray-700 [&_hr]:my-6
              [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6
              [&_p]:mb-3"
            dangerouslySetInnerHTML={{ __html: selectedEntry.content }}
          />
        </article>

        {selectedContest && (selectedContest.status === 'OPEN' || selectedContest.status === 'JUDGING') && user && selectedEntry.userId !== user.id && (
          <div className="flex justify-center mt-6">
            <button
              onClick={() => handleVote(selectedEntry.id)}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-colors ${
                selectedContest.userVotes?.includes(selectedEntry.id)
                  ? 'bg-ink-600 text-white'
                  : 'bg-gray-800 hover:bg-gray-700 text-gray-300'
              }`}
            >
              <svg className="w-5 h-5" fill={selectedContest.userVotes?.includes(selectedEntry.id) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              {selectedContest.userVotes?.includes(selectedEntry.id) ? 'Voted' : 'Vote for this entry'}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="text-center mb-10">
        <span className="text-5xl block mb-3">🏆</span>
        <h1 className="text-4xl font-extrabold mb-2">Monthly Writing Challenge</h1>
        <p className="text-gray-400 text-lg">Write your best story. Build readers. Win prizes.</p>
      </div>

      {selectedContest && (
        <div className="bg-gradient-to-r from-amber-900/30 to-orange-900/30 border border-amber-700/30 rounded-2xl p-6 md:p-8 mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${
                  selectedContest.status === 'OPEN' ? 'bg-green-600/20 text-green-400 border border-green-600/30' :
                  selectedContest.status === 'JUDGING' ? 'bg-yellow-600/20 text-yellow-400 border border-yellow-600/30' :
                  selectedContest.status === 'UPCOMING' ? 'bg-blue-600/20 text-blue-400 border border-blue-600/30' :
                  'bg-gray-600/20 text-gray-400 border border-gray-600/30'
                }`}>
                  {selectedContest.status}
                </span>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-ink-600/20 text-ink-400 border border-ink-600/30">
                  Any Genre
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold">{selectedContest.title}</h2>
              <p className="text-gray-300 mt-2 max-w-2xl">{selectedContest.description}</p>
            </div>
            <div className="text-right shrink-0">
              <div className="text-3xl font-extrabold text-amber-400">${selectedContest.prizePool}</div>
              <div className="text-sm text-gray-400">Total Prizes</div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-black/20 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-white">
                {selectedContest.status === 'OPEN' ? (
                  <CountdownTimer targetDate={selectedContest.endDate} />
                ) : selectedContest.status === 'UPCOMING' ? (
                  <CountdownTimer targetDate={selectedContest.startDate} />
                ) : selectedContest.status === 'JUDGING' ? (
                  <CountdownTimer targetDate={selectedContest.judgeEndDate} />
                ) : 'Finished'}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                {selectedContest.status === 'OPEN' ? 'Until Deadline' :
                 selectedContest.status === 'UPCOMING' ? 'Until Start' :
                 selectedContest.status === 'JUDGING' ? 'Until Results' : 'Completed'}
              </div>
            </div>
            <div className="bg-black/20 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-white">{selectedContest._count?.entries || 0}</div>
              <div className="text-xs text-gray-400 mt-1">Entries</div>
            </div>
            <div className="bg-black/20 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-white">{selectedContest.minWordCount}–{selectedContest.maxWordCount}</div>
              <div className="text-xs text-gray-400 mt-1">Word Range</div>
            </div>
            <div className="bg-black/20 rounded-xl p-4 text-center">
              <div className="text-lg font-bold text-white">{selectedContest.theme}</div>
              <div className="text-xs text-gray-400 mt-1">Theme</div>
            </div>
            {user && (selectedContest.status === 'OPEN' || selectedContest.status === 'JUDGING') && (
              <div className="bg-black/20 border border-ink-600/30 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-ink-400">{selectedContest.userVotes?.length || 0}</div>
                <div className="text-xs text-gray-400 mt-1">Your Votes</div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-6 mt-6 text-sm">
            <div>
              <span className="text-gray-400">1st Place: </span>
              <span className="text-amber-400 font-bold">${selectedContest.firstPrize}</span>
            </div>
            <div>
              <span className="text-gray-400">2nd Place: </span>
              <span className="text-gray-300 font-bold">${selectedContest.secondPrize}</span>
            </div>
            <div>
              <span className="text-gray-400">3rd Place: </span>
              <span className="text-amber-700 font-bold">${selectedContest.thirdPrize}</span>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
              Anti-fraud: 24h account age required
            </span>
            <span className="flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              Min 30s read time to count
            </span>
            <span className="flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
              Unique readers only
            </span>
          </div>
        </div>
      )}

      <div className="flex gap-1 mb-6 bg-gray-900 rounded-xl p-1">
        {[
          { key: 'current', label: 'Overview' },
          { key: 'entries', label: 'Entries' },
          ...(selectedContest?.status === 'OPEN' ? [{ key: 'submit', label: selectedContest?.userEntry ? 'Edit Entry' : 'Submit Entry' }] : []),
          { key: 'past', label: 'Past Contests' },
          ...(user?.role === 'ADMIN' ? [{ key: 'admin', label: 'Manage' }] : []),
        ].map((t: any) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              tab === t.key ? 'bg-ink-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'current' && selectedContest && (
        <div className="space-y-6">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
            <h3 className="text-xl font-bold mb-3">How Scoring Works</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-gray-800/50 rounded-xl p-4">
                <div className="text-2xl font-bold text-ink-400 mb-1">40%</div>
                <div className="font-semibold mb-1">Unique Reads</div>
                <p className="text-sm text-gray-400">Real readers who spend time with your story. Accounts must be 24h+ old with 30s minimum read time.</p>
              </div>
              <div className="bg-gray-800/50 rounded-xl p-4">
                <div className="text-2xl font-bold text-purple-400 mb-1">30%</div>
                <div className="font-semibold mb-1">Follower Engagement</div>
                <p className="text-sm text-gray-400">How many readers follow you after reading. Genuine interest from your audience matters.</p>
              </div>
              <div className="bg-gray-800/50 rounded-xl p-4">
                <div className="text-2xl font-bold text-amber-400 mb-1">30%</div>
                <div className="font-semibold mb-1">Community Votes</div>
                <p className="text-sm text-gray-400">Fellow readers and writers show their appreciation. One vote per person, verified accounts only.</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
            <h3 className="text-xl font-bold mb-3">Contest Rules</h3>
            <div className="text-gray-300 whitespace-pre-line leading-relaxed">{selectedContest.rules}</div>
          </div>

          {selectedContest.entries && selectedContest.entries.length > 0 && (
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
              <h3 className="text-xl font-bold mb-4">Top Entries</h3>
              <div className="space-y-3">
                {selectedContest.entries.slice(0, 5).map((entry: any, idx: number) => (
                  <button
                    key={entry.id}
                    onClick={() => readEntry(entry.id)}
                    className="w-full flex items-center gap-4 p-3 rounded-xl hover:bg-gray-800 transition-colors text-left"
                  >
                    <span className={`text-2xl font-extrabold ${
                      idx === 0 ? 'text-amber-400' : idx === 1 ? 'text-gray-300' : idx === 2 ? 'text-amber-700' : 'text-gray-500'
                    }`}>
                      #{idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{entry.title}</div>
                      <div className="text-sm text-gray-400">
                        by {entry.user.username}
                        <span className="ml-2 px-1.5 py-0.5 bg-gray-800 rounded text-xs">{entry.genre}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-400 shrink-0">
                      <span className="flex items-center gap-1" title="Reads">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        {entry.uniqueReads}
                      </span>
                      <span className="flex items-center gap-1" title="Votes">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                        {entry.votes}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
              {selectedContest.entries.length > 5 && (
                <button
                  onClick={() => setTab('entries')}
                  className="w-full mt-3 py-2 text-sm text-ink-400 hover:text-ink-300 transition-colors"
                >
                  View all {selectedContest.entries.length} entries
                </button>
              )}
            </div>
          )}

          {selectedContest.status === 'OPEN' && (
            <div className="bg-gradient-to-r from-ink-900/30 to-purple-900/30 border border-ink-700/30 rounded-2xl p-6 text-center">
              <h3 className="text-xl font-bold mb-2">Ready to compete?</h3>
              <p className="text-gray-400 mb-4">Any genre welcome. Write the story only you can tell.</p>
              <button
                onClick={() => {
                  if (!user) { onNavigate('login'); return; }
                  setTab('submit');
                }}
                className="px-6 py-3 bg-ink-600 hover:bg-ink-500 rounded-xl font-semibold transition-colors"
              >
                {selectedContest.userEntry ? 'Edit Your Entry' : 'Submit Your Entry'}
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'entries' && selectedContest && (
        <div className="space-y-3">
          {(!selectedContest.entries || selectedContest.entries.length === 0) ? (
            <div className="text-center py-16 bg-gray-900 rounded-2xl border border-gray-800">
              <span className="text-4xl block mb-3">📝</span>
              <p className="text-gray-400">No entries yet. Be the first to submit!</p>
            </div>
          ) : (
            selectedContest.entries.map((entry: any, idx: number) => (
              <div key={entry.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex items-center gap-4">
                <span className={`text-2xl font-extrabold w-10 text-center ${
                  entry.rank === 1 ? 'text-amber-400' :
                  entry.rank === 2 ? 'text-gray-300' :
                  entry.rank === 3 ? 'text-amber-700' :
                  'text-gray-500'
                }`}>
                  {entry.rank || idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <button
                    onClick={() => readEntry(entry.id)}
                    className="font-semibold hover:text-ink-400 transition-colors truncate block"
                  >
                    {entry.title}
                  </button>
                  <div className="text-sm text-gray-400 flex flex-wrap items-center gap-2">
                    <span>by {entry.user.username}</span>
                    <span className="px-1.5 py-0.5 bg-gray-800 rounded text-xs">{entry.genre}</span>
                    <span>{entry.wordCount.toLocaleString()} words</span>
                    {entry.prize ? <span className="text-amber-400 font-semibold">Won ${entry.prize}</span> : ''}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="flex items-center gap-1 text-sm text-gray-500" title="Reads">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    {entry.uniqueReads}
                  </span>
                  <button
                    onClick={() => readEntry(entry.id)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    Read
                  </button>
                  {(selectedContest.status === 'OPEN' || selectedContest.status === 'JUDGING') && user && entry.userId !== user?.id && (
                    <button
                      onClick={() => handleVote(entry.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        selectedContest.userVotes?.includes(entry.id)
                          ? 'bg-ink-600/20 text-ink-400'
                          : 'bg-gray-800 hover:bg-gray-700 text-gray-400'
                      }`}
                    >
                      <svg className="w-4 h-4" fill={selectedContest.userVotes?.includes(entry.id) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      {entry.votes}
                    </button>
                  )}
                  {(!user || entry.userId === user?.id) && (
                    <span className="flex items-center gap-1 text-sm text-gray-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      {entry.votes}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'submit' && selectedContest && (
        <div className="max-w-4xl mx-auto">
          {!user ? (
            <div className="text-center py-16 bg-gray-900 rounded-2xl border border-gray-800">
              <p className="text-gray-400 mb-4">Sign in to submit your contest entry</p>
              <button
                onClick={() => onNavigate('login')}
                className="px-6 py-3 bg-ink-600 hover:bg-ink-500 rounded-xl font-semibold transition-colors"
              >
                Sign In
              </button>
            </div>
          ) : (
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
              <h3 className="text-xl font-bold mb-1">
                {selectedContest.userEntry ? 'Edit Your Entry' : 'Submit Your Entry'}
              </h3>
              <p className="text-sm text-gray-400 mb-6">
                Any genre welcome · {selectedContest.minWordCount}–{selectedContest.maxWordCount} words
              </p>

              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Story Title</label>
                    <input
                      type="text"
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      placeholder="Give your story a compelling title"
                      className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 focus:border-ink-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Genre</label>
                    <select
                      value={genre}
                      onChange={e => setGenre(e.target.value)}
                      className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 focus:border-ink-500 focus:outline-none appearance-none"
                    >
                      {GENRES.map(g => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-gray-400">Story Content</label>
                    <span className={`text-sm font-medium ${
                      wordCount < selectedContest.minWordCount ? 'text-red-400' :
                      wordCount > selectedContest.maxWordCount ? 'text-red-400' :
                      'text-gray-400'
                    }`}>
                      {wordCount.toLocaleString()} / {selectedContest.maxWordCount.toLocaleString()} words
                      {wordCount < selectedContest.minWordCount && (
                        <span className="text-red-400 ml-2">
                          ({(selectedContest.minWordCount - wordCount).toLocaleString()} more needed)
                        </span>
                      )}
                    </span>
                  </div>
                  <RichTextEditor
                    value={content}
                    onChange={setContent}
                    placeholder="Begin writing your story..."
                    minHeight="500px"
                  />
                </div>

                {error && (
                  <div className="bg-red-900/20 border border-red-800 text-red-400 rounded-lg px-4 py-2 text-sm">
                    {error}
                  </div>
                )}
                {success && (
                  <div className="bg-green-900/20 border border-green-800 text-green-400 rounded-lg px-4 py-2 text-sm">
                    {success}
                  </div>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={submitting || !title.trim() || wordCount < selectedContest.minWordCount || wordCount > selectedContest.maxWordCount}
                  className="w-full py-3 bg-ink-600 hover:bg-ink-500 rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Submitting...' : selectedContest.userEntry ? 'Update Entry' : 'Submit Entry'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'past' && (
        <div className="space-y-6">
          {pastContests.length === 0 ? (
            <div className="text-center py-16 bg-gray-900 rounded-2xl border border-gray-800">
              <span className="text-4xl block mb-3">📚</span>
              <p className="text-gray-400">No past contests yet. This is the beginning!</p>
            </div>
          ) : (
            pastContests.map((contest: any) => (
              <div key={contest.id} className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold">{contest.title}</h3>
                    <p className="text-sm text-gray-400 mt-1">
                      {new Date(contest.startDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      {' · '}{contest._count.entries} entries
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-amber-400">${contest.prizePool}</div>
                  </div>
                </div>
                {contest.entries && contest.entries.length > 0 && (
                  <div className="space-y-2">
                    {contest.entries.map((entry: any) => (
                      <div key={entry.id} className="flex items-center gap-3 p-2">
                        <span className="text-xl">
                          {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : '🥉'}
                        </span>
                        <div className="flex-1">
                          <span className="font-medium">{entry.title}</span>
                          <span className="text-gray-400 text-sm ml-2">by {entry.user.username}</span>
                        </div>
                        {entry.prize && (
                          <span className="text-amber-400 font-semibold">${entry.prize}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'admin' && user?.role === 'ADMIN' && (
        <div className="space-y-6">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
            <h3 className="text-xl font-bold mb-4">{editingContestId ? 'Edit Contest' : 'Create New Contest'}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Title</label>
                <input
                  type="text"
                  value={adminForm.title}
                  onChange={e => setAdminForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-ink-500 focus:outline-none"
                  placeholder="March Writing Challenge"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Theme</label>
                <input
                  type="text"
                  value={adminForm.theme}
                  onChange={e => setAdminForm(f => ({ ...f, theme: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-ink-500 focus:outline-none"
                  placeholder="Open Theme"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <textarea
                  value={adminForm.description}
                  onChange={e => setAdminForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-ink-500 focus:outline-none"
                  rows={2}
                  placeholder="Contest description..."
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-400 mb-1">Rules</label>
                <textarea
                  value={adminForm.rules}
                  onChange={e => setAdminForm(f => ({ ...f, rules: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-ink-500 focus:outline-none"
                  rows={3}
                  placeholder="1. One entry per person..."
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Start Date</label>
                <input
                  type="datetime-local"
                  value={adminForm.startDate}
                  onChange={e => setAdminForm(f => ({ ...f, startDate: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-ink-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">End Date</label>
                <input
                  type="datetime-local"
                  value={adminForm.endDate}
                  onChange={e => setAdminForm(f => ({ ...f, endDate: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-ink-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Judge End Date</label>
                <input
                  type="datetime-local"
                  value={adminForm.judgeEndDate}
                  onChange={e => setAdminForm(f => ({ ...f, judgeEndDate: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-ink-500 focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Pool $</label>
                  <input
                    type="number"
                    value={adminForm.prizePool}
                    onChange={e => setAdminForm(f => ({ ...f, prizePool: Number(e.target.value) }))}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-white focus:border-ink-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">1st $</label>
                  <input
                    type="number"
                    value={adminForm.firstPrize}
                    onChange={e => setAdminForm(f => ({ ...f, firstPrize: Number(e.target.value) }))}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-white focus:border-ink-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">2nd $</label>
                  <input
                    type="number"
                    value={adminForm.secondPrize}
                    onChange={e => setAdminForm(f => ({ ...f, secondPrize: Number(e.target.value) }))}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-white focus:border-ink-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">3rd $</label>
                  <input
                    type="number"
                    value={adminForm.thirdPrize}
                    onChange={e => setAdminForm(f => ({ ...f, thirdPrize: Number(e.target.value) }))}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-white focus:border-ink-500 focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Min Words</label>
                <input
                  type="number"
                  value={adminForm.minWordCount}
                  onChange={e => setAdminForm(f => ({ ...f, minWordCount: Number(e.target.value) }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-ink-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Max Words</label>
                <input
                  type="number"
                  value={adminForm.maxWordCount}
                  onChange={e => setAdminForm(f => ({ ...f, maxWordCount: Number(e.target.value) }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-ink-500 focus:outline-none"
                />
              </div>
            </div>
            {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
            {success && <p className="text-emerald-400 text-sm mt-3">{success}</p>}
            <div className="flex gap-3 mt-6">
              <button
                onClick={async () => {
                  setError('');
                  setSuccess('');
                  try {
                    if (editingContestId) {
                      await api.contests.update(editingContestId, adminForm);
                      setSuccess('Contest updated!');
                    } else {
                      await api.contests.create(adminForm);
                      setSuccess('Contest created!');
                    }
                    setEditingContestId(null);
                    setAdminForm({ title: '', description: '', theme: '', rules: '', prizePool: 85, firstPrize: 50, secondPrize: 25, thirdPrize: 10, startDate: '', endDate: '', judgeEndDate: '', minWordCount: 500, maxWordCount: 5000 });
                    loadContests();
                  } catch (err: any) {
                    setError(err.message || 'Failed to save contest');
                  }
                }}
                className="px-6 py-2.5 bg-ink-600 hover:bg-ink-500 rounded-lg font-semibold transition-colors"
              >
                {editingContestId ? 'Update Contest' : 'Create Contest'}
              </button>
              {editingContestId && (
                <button
                  onClick={() => {
                    setEditingContestId(null);
                    setAdminForm({ title: '', description: '', theme: '', rules: '', prizePool: 85, firstPrize: 50, secondPrize: 25, thirdPrize: 10, startDate: '', endDate: '', judgeEndDate: '', minWordCount: 500, maxWordCount: 5000 });
                  }}
                  className="px-6 py-2.5 bg-gray-800 hover:bg-gray-700 rounded-lg font-medium transition-colors"
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
            <h3 className="text-xl font-bold mb-4">All Contests</h3>
            <div className="space-y-3">
              {contests.map((c: any) => (
                <div key={c.id} className="flex items-center justify-between p-4 bg-gray-800/50 rounded-xl">
                  <div>
                    <span className="font-medium">{c.title}</span>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                      c.status === 'OPEN' ? 'bg-green-600/20 text-green-400' :
                      c.status === 'UPCOMING' ? 'bg-blue-600/20 text-blue-400' :
                      c.status === 'JUDGING' ? 'bg-yellow-600/20 text-yellow-400' :
                      'bg-gray-600/20 text-gray-400'
                    }`}>
                      {c.status}
                    </span>
                    <span className="text-gray-400 text-sm ml-2">{c._count?.entries || 0} entries</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingContestId(c.id);
                        setAdminForm({
                          title: c.title, description: c.description, theme: c.theme, rules: c.rules,
                          prizePool: c.prizePool, firstPrize: c.firstPrize, secondPrize: c.secondPrize, thirdPrize: c.thirdPrize,
                          startDate: new Date(c.startDate).toISOString().slice(0, 16),
                          endDate: new Date(c.endDate).toISOString().slice(0, 16),
                          judgeEndDate: new Date(c.judgeEndDate).toISOString().slice(0, 16),
                          minWordCount: c.minWordCount, maxWordCount: c.maxWordCount,
                        });
                        window.scrollTo(0, 0);
                      }}
                      className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm transition-colors"
                    >
                      Edit
                    </button>
                    <select
                      value={c.status}
                      onChange={async (e) => {
                        try {
                          await api.contests.update(c.id, { status: e.target.value });
                          loadContests();
                        } catch (err: any) {
                          setError(err.message || 'Failed to update status');
                        }
                      }}
                      className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-1 text-sm text-white focus:outline-none"
                    >
                      <option value="UPCOMING">UPCOMING</option>
                      <option value="OPEN">OPEN</option>
                      <option value="JUDGING">JUDGING</option>
                      <option value="COMPLETED">COMPLETED</option>
                    </select>
                    <button
                      onClick={async () => {
                        if (confirm('Are you sure you want to delete this contest?')) {
                          try {
                            await api.contests.delete(c.id);
                            loadContests();
                          } catch (err: any) {
                            setError(err.message || 'Failed to delete');
                          }
                        }
                      }}
                      className="px-3 py-1.5 bg-red-900/50 hover:bg-red-800/50 text-red-400 rounded-lg text-sm transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {contests.length === 0 && (
                <p className="text-gray-400 text-center py-4">No contests yet. Create your first one above!</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
